/**
 * GiftCardSelector.tsx
 *
 * Descends from the legacy "Available Credit" section (payment-details-charge-
 * gateway.php / gift-credit-handeling2.js): auto-attaches every open-balance
 * patient-owned gift card on mount (legacy `checkForGiftCards(patientID)`) and
 * shows a green "$X.XX available" confirmation next to a serial-code search.
 *
 * Unlike legacy there is NO per-card amount slider: an applied card always
 * covers as much of the charge as it can — amounts are derived (never edited),
 * greedily allocated card-by-card up to `maxAmount` and re-derived whenever the
 * charge total or the applied set changes. Removing a card just hands its
 * share to the next one.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Gift } from 'lucide-react';
import {
  checkGiftCardBalance,
  listPatientGiftCards,
  type GiftCard,
} from '@/services/payments.service';
import { Button } from '@/components/ui/Button';
import { giftCardCodeErrorMessage } from '@/lib/giftCards';

export interface AppliedGiftCard {
  giftCard: GiftCard;
  amount: number;
}

interface GiftCardSelectorProps {
  appliedCards: AppliedGiftCard[];
  onChange: (cards: AppliedGiftCard[]) => void;
  maxAmount: number;
  /** Patient context — enables auto-attach of patient-owned cards + patient dropdown */
  patientId?: number;
  /** Allow manual serial-code search (default true) */
  allowSerialSearch?: boolean;
  /** When true, shows applied cards only (no add/remove/slider/search). */
  readOnly?: boolean;
  /** Read-only rows get a "View gift card" link that calls this with the card's id. */
  onViewCard?: (giftCardId: number) => void;
}

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function GiftCardSelector({
  appliedCards,
  onChange,
  maxAmount,
  patientId,
  allowSerialSearch = true,
  readOnly = false,
  onViewCard,
}: GiftCardSelectorProps) {
  const [searchCode, setSearchCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastFound, setLastFound] = useState<GiftCard | null>(null);

  const totalApplied = appliedCards.reduce((sum, c) => sum + c.amount, 0);
  const remaining = Math.max(0, maxAmount - totalApplied);

  // ─── Auto-attach patient-owned cards on mount ─────────────────────────
  const { data: patientCards = [], isLoading: loadingPatientCards } = useQuery({
    queryKey: ['patient-gift-cards', patientId],
    queryFn: () => listPatientGiftCards(patientId!),
    enabled: !readOnly && !!patientId && patientId > 0,
    staleTime: 60_000,
  });

  const autoAttachedRef = useRef(false);

  useEffect(() => {
    if (readOnly) return;
    if (autoAttachedRef.current) return;
    if (loadingPatientCards) return;
    if (!patientCards.length) {
      autoAttachedRef.current = true;
      return;
    }
    autoAttachedRef.current = true;

    const existingIds = new Set(appliedCards.map((c) => c.giftCard.paymentsCreditID));
    const toAdd: AppliedGiftCard[] = [];
    let budget = Math.max(0, maxAmount - totalApplied);

    for (const gc of patientCards) {
      if (gc.remainingBalance <= 0) continue;
      if (existingIds.has(gc.paymentsCreditID)) continue;
      const amount = Math.min(gc.remainingBalance, budget);
      if (amount <= 0) {
        // Still attach at $0 so the card shows up in the list (legacy keeps it visible)
        toAdd.push({ giftCard: gc, amount: 0 });
        continue;
      }
      toAdd.push({ giftCard: gc, amount });
      budget = Math.max(0, budget - amount);
    }

    if (toAdd.length > 0) {
      onChange([...appliedCards, ...toAdd]);
    }
    // Intentionally ignore onChange/appliedCards deps — we only auto-attach once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientCards, loadingPatientCards, readOnly]);

  // ─── Derive applied amounts from the charge total ─────────────────────
  // Amounts are never user-edited: each card takes as much of the remaining
  // charge as its balance allows, in applied order. Re-runs on any change to
  // the applied set or maxAmount, so amounts grow AND shrink to always cover
  // the maximum needed.
  useEffect(() => {
    if (readOnly) return;
    let remainingBudget = Math.max(0, maxAmount);
    let changed = false;

    const normalizedCards = appliedCards.map((card) => {
      const nextAmount = Math.max(
        0,
        Math.min(card.giftCard.remainingBalance, remainingBudget),
      );
      remainingBudget = Math.max(0, remainingBudget - nextAmount);
      if (nextAmount !== card.amount) changed = true;
      return { ...card, amount: nextAmount };
    });

    if (changed) onChange(normalizedCards);
  }, [appliedCards, maxAmount, onChange, readOnly]);

  // ─── Patient-owned cards available for manual add via dropdown ────────
  const availablePatientCards = useMemo(() => {
    const attachedIds = new Set(appliedCards.map((c) => c.giftCard.paymentsCreditID));
    return patientCards.filter((gc) => !attachedIds.has(gc.paymentsCreditID));
  }, [patientCards, appliedCards]);

  // The "$X available" confirmation belongs to the card it was shown for — when
  // that card is removed (or the applied set is replaced by the parent) the
  // message goes with it, instead of lingering as if a card were still applied.
  const foundStillApplied = useMemo(
    () =>
      lastFound !== null &&
      appliedCards.some(
        (c) => c.giftCard.paymentsCreditID === lastFound.paymentsCreditID,
      ),
    [lastFound, appliedCards],
  );

  async function handleSearch() {
    const code = searchCode.trim();
    if (!code) return;
    setSearching(true);
    setSearchError(null);
    setLastFound(null);
    try {
      // The patient paying goes with the lookup so the API applies the owner
      // rule: another patient's card is refused, and an unclaimed card is
      // claimed for this patient when the payment is saved (R13, R20).
      const gc = await checkGiftCardBalance(code, patientId);
      if (!gc || gc.remainingBalance <= 0) {
        setSearchError('Gift card not found or has no remaining balance.');
        return;
      }
      if (appliedCards.some((c) => c.giftCard.paymentsCreditID === gc.paymentsCreditID)) {
        setSearchError('This gift card is already applied.');
        return;
      }
      const applyAmount = Math.min(gc.remainingBalance, remaining);
      onChange([...appliedCards, { giftCard: gc, amount: applyAmount }]);
      setLastFound(gc);
      setSearchCode('');
    } catch (err) {
      setSearchError(giftCardCodeErrorMessage(err));
    } finally {
      setSearching(false);
    }
  }

  function attachPatientCard(giftCardId: number) {
    const gc = availablePatientCards.find((c) => c.paymentsCreditID === giftCardId);
    if (!gc) return;
    const applyAmount = Math.min(gc.remainingBalance, remaining);
    onChange([...appliedCards, { giftCard: gc, amount: applyAmount }]);
  }

  function removeCard(index: number) {
    onChange(appliedCards.filter((_, i) => i !== index));
  }

  if (readOnly) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
            <Gift className="h-4 w-4 text-slate-400" />
            Gift Cards
          </span>
          {totalApplied > 0 ? (
            <span className="text-xs text-green-700 font-medium">
              -{formatCurrency(totalApplied)} applied
            </span>
          ) : null}
        </div>
        {appliedCards.length === 0 ? (
          <p className="text-xs italic text-slate-400">No gift cards applied.</p>
        ) : (
          appliedCards.map((card) => (
            <div
              key={card.giftCard.paymentsCreditID}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm"
            >
              <div className="font-medium text-slate-800">
                {card.giftCard.serialNumber || '—'}
                {card.giftCard.title ? (
                  <span className="ml-2 font-normal text-slate-500">{card.giftCard.title}</span>
                ) : null}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                <span className="flex-1">
                  Applied {formatCurrency(card.amount)}
                  {` · balance ${formatCurrency(card.giftCard.remainingBalance)}`}
                </span>
                {onViewCard && (card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID) > 0 ? (
                  <button
                    type="button"
                    className="font-medium text-blue-600 hover:underline"
                    onClick={() => onViewCard(card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID)}
                  >
                    View gift card
                  </button>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <Gift className="h-4 w-4 text-slate-400" />
          Gift Cards
        </span>
        {/* Total only when several cards contribute — with one card the row
            below already shows the same figure. */}
        {totalApplied > 0 && appliedCards.filter((c) => c.amount > 0).length > 1 && (
          <span className="text-xs text-green-700 font-medium">
            -{formatCurrency(totalApplied)} applied
          </span>
        )}
      </div>

      {/* Applied rows — amount is derived (max the card can cover), no editing */}
      {appliedCards.map((card, i) => {
        const balance = card.giftCard.remainingBalance;
        return (
          <div
            key={card.giftCard.paymentsCreditID}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm"
          >
            <div className="flex items-center gap-2">
              <span className="flex-1 text-slate-700">
                <span className="font-medium">{card.giftCard.serialNumber || '—'}</span>
                <span className="ml-2 text-slate-500">
                  balance: {formatCurrency(balance)}
                </span>
              </span>
              {card.amount > 0 ? (
                <span className="text-xs font-medium text-green-700 tabular-nums">
                  -{formatCurrency(card.amount)} applied
                </span>
              ) : (
                <span className="text-xs text-slate-400">not needed</span>
              )}
              <Button size="xs" variant="ghost" onClick={() => removeCard(i)}>
                Remove
              </Button>
            </div>
          </div>
        );
      })}

      {/* Patient-owned dropdown — attach additional cards the patient owns */}
      {availablePatientCards.length > 0 && (
        <div className="flex gap-2">
          <select
            value=""
            onChange={(e) => {
              const id = Number(e.target.value);
              if (id) attachPatientCard(id);
            }}
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">
              Patient cards ({availablePatientCards.length} available)
            </option>
            {availablePatientCards.map((gc) => (
              <option key={gc.paymentsCreditID} value={gc.paymentsCreditID}>
                {gc.serialNumber} — {formatCurrency(gc.remainingBalance)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Serial search — non-patient cards */}
      {allowSerialSearch && (
        <div className="space-y-1">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchCode}
              onChange={(e) => {
                setSearchCode(e.target.value);
                setLastFound(null);
                setSearchError(null);
              }}
              placeholder="Enter gift card code..."
              onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
              className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handleSearch()}
              disabled={searching}
            >
              {searching ? 'Searching...' : 'Add'}
            </Button>
          </div>
          {foundStillApplied && lastFound && (
            <p className="flex items-center gap-1 text-xs text-green-700 font-medium">
              <Check className="h-3.5 w-3.5" />
              {formatCurrency(lastFound.remainingBalance)} available
            </p>
          )}
          {searchError && <p className="text-xs text-red-600">{searchError}</p>}
        </div>
      )}
    </div>
  );
}
